import { Link } from "react-router-dom";
import { ArrowRight, Building2, Factory, ScanLine, ShieldCheck, CheckCircle2 } from "lucide-react";

const ACTORS = [
  { icon: Building2, t: "Commerçant", d: "Créez et suivez votre projet d'enseigne étape par étape avec pré-contrôle automatique." },
  { icon: ShieldCheck, t: "Mairie", d: "Instruisez les demandes, configurez vos règlements locaux, validez ou refusez." },
  { icon: Factory, t: "Fabricant", d: "Recevez des demandes qualifiées, envoyez vos devis et suivez vos commandes." },
  { icon: ScanLine, t: "Agent de contrôle", d: "Contrôlez sur le terrain que l'enseigne installée correspond au projet validé." },
];

const STEPS = ["Demande", "Pré-analyse", "Dépôt mairie", "Validation", "Fabricant", "Devis", "Commande", "Installation", "Contrôle", "Clôture"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="h-16 bg-white border-b border-border flex items-center justify-between px-6 lg:px-12 sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#000091] flex items-center justify-center">
            <span className="text-white font-black text-lg">E</span>
          </div>
          <span className="font-display font-black text-xl tracking-tight">ENSEIGNE SMART</span>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <a href="#acteurs" className="hover:text-[#000091] transition-colors">Acteurs</a>
          <a href="#workflow" className="hover:text-[#000091] transition-colors">Processus</a>
          <a href="#pourquoi" className="hover:text-[#000091] transition-colors">Avantages</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="header-login-btn" className="text-sm font-semibold hover:text-[#000091] transition-colors">Connexion</Link>
          <Link to="/register" data-testid="header-register-btn" className="bg-[#000091] text-white px-4 py-2 text-sm font-semibold hover:bg-blue-900 transition-colors flex items-center gap-1">
            S'inscrire <ArrowRight size={15} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="grid lg:grid-cols-2">
          <div className="px-6 lg:px-12 py-16 lg:py-24">
            <div className="inline-flex items-center gap-2 border border-[#000091]/20 bg-blue-50 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[#000091] mb-6">
              Plateforme SaaS · France
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none mb-6">
              La gestion des enseignes commerciales, <span className="text-[#000091]">entièrement digitalisée.</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mb-8">
              De la demande du commerçant jusqu'au contrôle terrain de l'agent : un processus unique, traçable et conforme reliant commerçants, mairies, fabricants et agents.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-btn" className="bg-[#000091] text-white px-6 py-3 font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2">
                Démarrer un projet <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="border border-[#000091] text-[#000091] px-6 py-3 font-semibold hover:bg-[#000091] hover:text-white transition-colors">
                Espace professionnel
              </Link>
            </div>
          </div>
          <div className="relative min-h-[300px] lg:min-h-full">
            <img src="https://images.unsplash.com/photo-1646919637321-8d9644dc16ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Devanture commerciale française" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#000091]/10" />
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="px-6 lg:px-12 py-16 bg-white border-b border-border">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Le cœur du produit</div>
        <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-8">Un workflow métier de bout en bout</h2>
        <div className="flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className="border border-border bg-[#F4F4F5] px-4 py-2 text-sm font-semibold">{s}</div>
              {i < STEPS.length - 1 && <ArrowRight size={16} className="text-slate-400" />}
            </div>
          ))}
        </div>
      </section>

      {/* Actors */}
      <section id="acteurs" className="px-6 lg:px-12 py-16 border-b border-border">
        <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-10">Quatre acteurs, une plateforme</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ACTORS.map((a) => (
            <div key={a.t} className="card-flat p-6 hover:border-[#000091] transition-colors">
              <a.icon size={28} className="text-[#000091] mb-4" />
              <h3 className="font-display font-bold text-xl mb-2">{a.t}</h3>
              <p className="text-sm text-slate-600">{a.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi */}
      <section id="pourquoi" className="px-6 lg:px-12 py-16 bg-[#000091] text-white">
        <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-10">D'un processus manuel à une plateforme maîtrisée</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {["Digital & centralisé", "Pré-contrôle de conformité automatique", "Traçabilité complète (audit log)", "Multi-collectivités (multi-tenant)", "Mise en relation avec fabricants", "Contrôle terrain mobile + GPS"].map((x) => (
            <div key={x} className="flex items-center gap-3">
              <CheckCircle2 size={20} className="text-[#FFD600] shrink-0" />
              <span className="font-medium">{x}</span>
            </div>
          ))}
        </div>
      </section>

      <footer className="px-6 lg:px-12 py-8 bg-white text-sm text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
        <span>© 2026 ENSEIGNE SMART — Plateforme de démonstration</span>
        <span>Conçu pour la France · RGPD Privacy by Design</span>
      </footer>
    </div>
  );
}
